/**
 * The live ArchivePort: the agent's read tools backed by the real pan.do/ra
 * client (src/api/pandora.ts).
 *
 * Kept apart from search.ts so the tools themselves stay transport-free — evals
 * and unit tests hand in a fixture-backed port instead, and nothing in the tool
 * code changes.
 */
import { archiveLayerMap, layerMapFromSiteInfo, type Archive } from '../../api/archives';
import {
  findAnnotationsAdvanced,
  findItemsAdvanced,
  getItem,
  getLayers,
  getSiteInfo,
  type PandoraObserver,
  type PandoraQuery,
} from '../../api/pandora';
import type {
  ArchiveAnnotation,
  ArchiveItem,
  ArchiveItemDetail,
  ArchiveLayer,
  ArchivePort,
} from './catalog';

/**
 * `observe` is how the assistant's step inspector gets at the exact archive
 * request behind each tool call (docs/agent.md §4). This module is the only
 * place allowed to pass it: threading it explicitly, rather than setting a
 * module-level observer inside api/pandora.ts, is what keeps the rest of the
 * app's archive traffic — SearchPanel, BinPanel, ItemDetail, partoffsets — from
 * being misattributed to whichever tool happens to be in flight.
 */
export function createArchivePort(
  archives: Archive[],
  observe?: PandoraObserver,
): ArchivePort {
  const siteFor = (key: string): string => {
    const archive = archives.find((a) => a.key === key);
    if (!archive) throw new Error(`unknown archive "${key}"`);
    return archive.site;
  };

  // Per-instance config is fetched once and reused: itemKeys is consulted on
  // every search_items call, and re-fetching `init` each time would double the
  // latency of the agent's most common tool.
  const keyCache = new Map<string, Promise<string[]>>();
  const layerCache = new Map<string, Promise<string[]>>();
  const layerListCache = new Map<string, Promise<ArchiveLayer[]>>();

  return {
    async findItems(archive, query, range, sort) {
      const items = await findItemsAdvanced(
        siteFor(archive),
        { query: query as PandoraQuery, range, sort },
        observe,
      );
      return items as ArchiveItem[];
    },

    async findAnnotations(archive, opts) {
      const rows = await findAnnotationsAdvanced(
        siteFor(archive),
        {
          query: opts.query as PandoraQuery,
          ...(opts.itemsQuery ? { itemsQuery: opts.itemsQuery as PandoraQuery } : {}),
          range: opts.range,
        },
        observe,
      );
      return rows as ArchiveAnnotation[];
    },

    async getItem(archive, item) {
      const detail = await getItem(siteFor(archive), item, observe);
      return detail as ArchiveItemDetail;
    },

    async getLayers(archive, item) {
      const layers = await getLayers(siteFor(archive), item, observe);
      return layers as Record<string, ArchiveAnnotation[]>;
    },

    async itemKeys(archive) {
      let pending = keyCache.get(archive);
      if (!pending) {
        pending = getSiteInfo(siteFor(archive), observe)
          .then((info) => info.itemKeys.map((k) => k.id))
          // An instance that will not serve `init` should degrade to "no
          // filters available" rather than break search entirely.
          .catch(() => []);
        keyCache.set(archive, pending);
      }
      return pending;
    },

    async subtitleLayers(archive) {
      let pending = layerCache.get(archive);
      if (!pending) {
        pending = getSiteInfo(siteFor(archive), observe)
          .then((info) => layerMapFromSiteInfo(info).subtitleLayers)
          // Fall back to the hardcoded per-archive table, which covers the
          // three known public instances (api/archives.ts).
          .catch(() => archiveLayerMap(archive).subtitleLayers);
        layerCache.set(archive, pending);
      }
      return pending;
    },

    async layers(archive) {
      let pending = layerListCache.get(archive);
      if (!pending) {
        pending = getSiteInfo(siteFor(archive), observe)
          .then((info) => info.layers as ArchiveLayer[])
          // Same degradation as itemKeys: an instance that will not serve
          // `init` should cost the prompt a layer list, not the whole run.
          .catch(() => []);
        layerListCache.set(archive, pending);
      }
      return pending;
    },
  };
}
